import React, { useState, useRef, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import '../styles/Mypaper.css';
import { FaBell, FaUserCircle, FaDownload, FaSearch, FaTrash } from "react-icons/fa";
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from "react-router-dom";

const Mypaper = () => {
  const navigate = useNavigate();
  const [activeItem, setActiveItem] = useState("/papers/mypapers");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Filters
  const [board, setBoard] = useState('');
  const [grade, setGrade] = useState('');
  const [subject, setSubject] = useState('');
  const [date, setDate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Data
  const [drafts, setDrafts] = useState([]);
  const [selectedDrafts, setSelectedDrafts] = useState([]);
  const [boards, setBoards] = useState([]);
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false); // NEW

  const [dashboardData, setDashboardData] = useState({
    totalBoard: 0,
    totalGrade: 0,
    totalSubjects: 0,
    totalPapers: 0,
    totalQuestions: 0,
  });

  const [user, setUser] = useState({
    id: null,
    name: '',
    email: '',
    password: '',
  });

  // Fetch dashboard & user info
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get("https://digital.tifleducation.com/admin/public/api/getDashboard", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data) {
          setDashboardData({
            totalBoard: res.data.totalBoards || 0,
            totalGrade: res.data.totalGrades || 0,
            totalSubjects: res.data.totalSubjects || 0,
            totalPapers: res.data.totalPapers || 0,
            totalQuestions: res.data.totalQuestions || 0,
          });
          if (res.data.user) {
            setUser({
              id: res.data.user.id,
              name: res.data.user.name || '',
              email: res.data.user.email || '',
              password: '',
            });
          }
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error.response?.data || error);
      }
    };
    fetchDashboardData();
  }, []);

  // Fetch papers with filters
  const fetchPapers = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get("https://digital.tifleducation.com/admin/public/api/getPapers", {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          board: board ? Number(board) : null,
          grade: grade ? Number(grade) : null,
          subject: subject ? Number(subject) : null,
          date: date || null,
          search: searchQuery || null,
        },
      });

      if (res.data?.data?.data) {
        setDrafts(res.data.data.data);

        // Set filters if returned
        if (res.data.filters) {
          setBoards(res.data.filters.boards || []);
          setGrades(res.data.filters.grades || []);
          setSubjects(res.data.filters.subjects || []);
        }
      }
    } catch (error) {
      console.error("Error fetching papers:", error.response?.data || error);
      toast.error("Failed to fetch papers");
    }
  };

  // Fetch papers whenever filter changes
  useEffect(() => {
    fetchPapers();
  }, [board, grade, subject, date, searchQuery]);

  // Profile save
  const handleSaveProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post("https://digital.tifleducation.com/admin/public/api/update-profile",
        { name: user.name, password: user.password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Profile updated successfully!");
      setIsModalOpen(false);
      setUser({ ...user, password: '' });
    } catch (error) {
      console.error("Error updating profile:", error.response?.data || error);
      toast.error("Failed to update profile");
    }
  };

  const toggleDropdown = () => setShowDropdown(prev => !prev);

  const handleSelectAll = (e) => {
    if (e.target.checked) setSelectedDrafts(drafts.map(d => d.id));
    else setSelectedDrafts([]);
  };

  const handleCheckboxChange = (id) => {
    setSelectedDrafts(prev => prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]);
  };

  // ---- DELETE ----
  const handleDeleteConfirm = async () => {
    if (selectedDrafts.length === 0) {
      toast.warning("Please select at least one paper to delete");
      setIsDeleteModalOpen(false);
      return;
    }
    try {
      const token = localStorage.getItem("token");
      await Promise.all(
        selectedDrafts.map((id) =>
          axios.delete(`https://digital.tifleducation.com/admin/public/api/delete-papers/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        )
      );
      setDrafts(prev => prev.filter(d => !selectedDrafts.includes(d.id)));
      setSelectedDrafts([]);
      toast.success("Selected paper(s) deleted successfully!");
    } catch (error) {
      console.error("Error deleting papers:", error.response?.data || error);
      toast.error("Failed to delete papers");
    } finally {
      setIsDeleteModalOpen(false);
    }
  };

  const handleExportPaper = async (paperId) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`https://digital.tifleducation.com/admin/public/api/papers/export/${paperId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "json",
      });

      if (res.data.status === "success" && res.data.file_url) {
        window.open(res.data.file_url, "_blank");
        toast.success("Paper PDF opened in new tab!");
        localStorage.removeItem("selected_questions");
        localStorage.removeItem("board_id");
        localStorage.removeItem("grade_id");
        localStorage.removeItem("subject_id");
        setTimeout(() => navigate("/papers/mypapers"), 500);
      } else {
        toast.error("Failed to generate PDF");
      }
    } catch (error) {
      console.error("Error exporting paper:", error.response?.data || error);
      toast.error("Failed to export paper");
    }
  };

  const handleExportAnswerKey = async (paperId) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`https://digital.tifleducation.com/admin/public/api/papers/export-answer-key/${paperId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "json",
      });

      if (res.data.status === "success" && res.data.file_url) {
        window.open(res.data.file_url, "_blank");
        toast.success("Answer Key PDF opened in new tab!");
        localStorage.removeItem("selected_questions");
        localStorage.removeItem("board_id");
        localStorage.removeItem("grade_id");
        localStorage.removeItem("subject_id");
        setTimeout(() => navigate("/papers/mypapers"), 500);
      } else {
        toast.error("Failed to generate answer key PDF");
      }
    } catch (error) {
      console.error("Error exporting answer key:", error.response?.data || error);
      toast.error("Failed to export answer key");
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setShowDropdown(false);
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => document.removeEventListener("click", handleClickOutside, true);
  }, []);

  // ---- FILTERED DRAFTS ----
  const filteredDrafts = drafts.filter(paper => {
    const matchesSearch = !searchQuery || paper.paper_name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesGrade = !grade || paper.grade === grades.find(g => g.id == grade)?.name;
    const matchesSubject = !subject || paper.subject == subject;
    const matchesDate = !date || (paper.date && paper.date.split('-').reverse().join('-') === date);
    return matchesSearch && matchesGrade && matchesSubject && matchesDate;
  });

  return (
    <div className="dashboard-container">
      <Sidebar
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        activeItem={activeItem}
        setActiveItem={setActiveItem}
      />

      <main className="main-content">
        {/* Header */}
        <div className="mypaper-header">
          <div className="mypaper-welcome-text">
            <a href="/dashboard" className="back-link">
              <i className="fa fa-arrow-left" aria-hidden="true"></i>
            </a>
            <span className="mypaper-welcome-text">My Papers</span>
          </div>
          <div className="header-icons" ref={dropdownRef}>
            <FaBell className="icon" />
            <div className="user-dropdown-wrapper">
              <FaUserCircle className="icon" onClick={toggleDropdown} />
              {showDropdown && (
                <div className="user-dropdown">
                  <div className='edituser' onClick={() => setIsModalOpen(true)}>
                    <FaUserCircle className="ditusericon" />
                    <span>Edit Profile</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="draft-dropdown-row">
          <div className="draft-dropdown-group">
            <label>Grade</label>
            <select value={grade} onChange={(e) => setGrade(e.target.value)}>
              <option value="">Select Grade</option>
              {grades.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>

          <div className="draft-dropdown-group">
            <label>Subject</label>
            <select value={subject} onChange={(e) => setSubject(e.target.value)}>
              <option value="">Select Subject</option>
              {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>

          <div className="draft-dropdown-group">
            <label>Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="draft-input-field"
            />
          </div>

          <div className="draft-search-bar-container">
            <div className="search-input-wrapper">
              <FaSearch className="search-icon" />
              <input
                type="text"
                className="search-input"
                placeholder="Search paper..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Draft List Header */}
        {filteredDrafts.length > 0 && (
          <div className="draft-list-header">
            <div className="left">
              <input
                type="checkbox"
                checked={selectedDrafts.length === filteredDrafts.length && filteredDrafts.length > 0}
                onChange={handleSelectAll}
              />
              <span className="select-all-label">Select All</span>
            </div>
            {selectedDrafts.length > 0 && (
              <button className="delete-btn" onClick={() => setIsDeleteModalOpen(true)}>
                <FaTrash /> Delete
              </button>
            )}
          </div>
        )}

        {/* Draft List */}
        {filteredDrafts.length === 0 ? (
          <p className="no-data-myppr">No papers found.</p>
        ) : (
          <div className="myppr-list">
            {filteredDrafts.map(paper => (
              <div key={paper.id} className="myppr-item">
                <div className="left">
                  <input
                    type="checkbox"
                    checked={selectedDrafts.includes(paper.id)}
                    onChange={() => handleCheckboxChange(paper.id)}
                  />
                  <div className="myppr-info">
                    <span className="myppr-name">{paper.paper_name}</span>
                    <span className="myppr-date">{paper.date}</span>
                  </div>
                </div>
                <div className="exprtppr">
                  <button className="export-btn" onClick={() => handleExportPaper(paper.id)}>Export Paper</button>
                  <button className="export-btn" onClick={() => handleExportAnswerKey(paper.id)}>Export Answer Key</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Profile Modal */}
      {isModalOpen && (
        <div style={modalStyles.overlay}>
          <div style={modalStyles.modal}>
            <button style={modalStyles.closeBtn} onClick={() => setIsModalOpen(false)}>&times;</button>
            <div style={modalStyles.userIcon}><FaUserCircle /></div>
            <div style={modalStyles.formGroup}>
              <label>Username</label>
              <input type="text" value={user.name} onChange={(e) => setUser({ ...user, name: e.target.value })} style={modalStyles.input} />
            </div>
            <div style={modalStyles.formGroup}>
              <label>Email</label>
              <input type="email" value={user.email} disabled style={{ ...modalStyles.input, background: '#f0f0f0', color: '#888' }} />
            </div>
            <div style={modalStyles.formGroup}>
              <label>Password</label>
              <input type="password" value={user.password} onChange={(e) => setUser({ ...user, password: e.target.value })} style={modalStyles.input} />
            </div>
            <div style={{ textAlign: 'right' }}>
              <button style={modalStyles.saveBtn} onClick={handleSaveProfile}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete Modal */}
      {isDeleteModalOpen && (
        <div style={modalStyles.overlay}>
          <div style={modalStyles.modal}>
            <h3>Are you sure you want to delete the selected paper(s)?</h3>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1rem" }}>
              <button
                style={{ ...modalStyles.saveBtn, background: "#ccc", color: "#000", marginRight: "10px" }}
                onClick={() => setIsDeleteModalOpen(false)}
              >
                Cancel
              </button>
              <button style={modalStyles.saveBtn} onClick={handleDeleteConfirm}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const modalStyles = {
  overlay: {
    position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
    background: "rgba(0,0,0,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000
  },
  modal: {
    background: "#fff", padding: "2rem", borderRadius: "12px", width: "90%", maxWidth: "400px", position: "relative",
    boxShadow: "0 4px 15px rgba(0,0,0,0.2)"
  },
  closeBtn: {
    position: "absolute", top: "10px", right: "15px", background: "none", border: "none", fontSize: "1.5rem", cursor: "pointer"
  },
  userIcon: { textAlign: "center", fontSize: "4rem", color: "#129255", marginBottom: "1rem" },
  formGroup: { marginBottom: "1rem" },
  input: { width: "100%", padding: "0.5rem 0.8rem", borderRadius: "6px", border: "1px solid #ccc", outline: "none" },
  saveBtn: { background: "#129255", color: "#fff", border: "none", padding: "0.6rem 1.2rem", borderRadius: "6px", cursor: "pointer", fontWeight: "bold" }
};

export default Mypaper;
